import { Hanko, register, type SessionCheckResponse } from '@teamhanko/hanko-elements'
import { all as hankoTranslations } from '@teamhanko/hanko-elements/i18n/all'
import { getHankoConfig } from '@/config/runtime'

type HankoUser = {
  id?: string
  user_id?: string
  email?: string
  emails?: Array<{ address?: string; is_primary?: boolean; is_verified?: boolean }>
  username?: string | { username?: string }
  name?: string
  given_name?: string
  family_name?: string
  picture?: string
}

type HankoCurrentUser = Partial<HankoUser>

let hanko: Hanko | null = null
let registerPromise: Promise<void> | null = null

const SUPPORTED_HANKO_LANGUAGES = new Set([
  'bn',
  'de',
  'en',
  'fr',
  'it',
  'nl',
  'ptBR',
  'zh',
])

function getHankoAPIURL(): string {
  const { apiUrl } = getHankoConfig()
  if (!apiUrl) {
    throw new Error('Hanko API URL is not configured')
  }
  return apiUrl
}

export function getHankoClient(): Hanko {
  if (hanko) return hanko
  hanko = new Hanko(getHankoAPIURL())
  return hanko
}

export function registerHankoElements(): Promise<void> {
  if (!registerPromise) {
    registerPromise = register(getHankoAPIURL(), {
      fallbackLanguage: 'en',
      translations: hankoTranslations,
    }).then(() => undefined)
  }
  return registerPromise
}

export function getHankoElementsLanguage(language?: string | null): string {
  const normalized = (language || '').trim().replace('_', '-')
  if (!normalized) return 'en'

  const [base] = normalized.split('-')
  const resolved =
    normalized.toLowerCase() === 'pt-br'
      ? 'ptBR'
      : base.toLowerCase() === 'zh'
        ? 'zh'
        : base.toLowerCase()

  return SUPPORTED_HANKO_LANGUAGES.has(resolved) ? resolved : 'en'
}

export async function getAuthToken(): Promise<string | null> {
  try {
    return (await getHankoClient().getSessionToken()) || null
  } catch {
    return null
  }
}

function normalizeUsername(
  username: HankoUser['username'] | undefined,
  fallback?: string,
): HankoUser['username'] | undefined {
  if (typeof username === 'string') return username
  if (typeof username?.username === 'string') return username.username
  return fallback
}

async function getSessionClaims(): Promise<SessionCheckResponse | null> {
  try {
    return await getHankoClient().validateSession()
  } catch {
    return null
  }
}

export async function getCurrentAuthUser(): Promise<HankoUser | null> {
  try {
    const currentUser = (await getHankoClient().getCurrentUser()) as unknown as HankoCurrentUser
    const session = await getSessionClaims()
    const claims = session?.claims
    const userId = currentUser.user_id || currentUser.id || session?.user_id || claims?.subject
    if (!userId) return null

    const claimEmail = claims?.email
    const emails = currentUser.emails?.length
      ? currentUser.emails
      : claimEmail?.address
        ? [{
            address: claimEmail.address,
            is_primary: claimEmail.is_primary,
            is_verified: claimEmail.is_verified,
          }]
        : undefined

    return {
      ...currentUser,
      id: currentUser.id || userId,
      user_id: currentUser.user_id || userId,
      email: currentUser.email || claimEmail?.address,
      emails,
      username: normalizeUsername(currentUser.username, claims?.username),
    }
  } catch {
    return null
  }
}

export async function validateSession(): Promise<boolean> {
  return Boolean(await getAuthToken())
}

export async function signOut() {
  await getHankoClient().logout()
}

export function onSessionCreated(callback: () => void): () => void {
  return getHankoClient().onSessionCreated(callback)
}

export function onSessionExpired(callback: () => void): () => void {
  return getHankoClient().onSessionExpired(callback)
}

export function getPrimaryEmail(user: HankoUser | null | undefined): string {
  if (!user) return ''
  if (typeof user.email === 'string') return user.email
  const primary = user.emails?.find((email) => email.is_primary)
  return primary?.address || user.emails?.[0]?.address || ''
}

export type { HankoUser }
